import type { TableColumnsType, TablePaginationConfig } from 'antd'
import { Button, Flex, message, Table, Tooltip } from 'antd'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { FaBan, FaUserEdit } from 'react-icons/fa'
import { GoLock } from "react-icons/go"
import { IoCheckmarkDoneOutline } from "react-icons/io5"
import { Link } from 'react-router-dom'

import { SorterResult } from 'antd/es/table/interface'
import { FilterValue } from 'antd/lib/table/interface'
import { useAttemptListener } from 'dev-masters-react-kit'
import { listenerMiddleware, store, useAppDispatch, useAppSelector } from '../../utils/redux/store'
import { attemptGetUsers, GetListUsersDto, OrderDirection, User, UsersOrderBy } from '../../utils/redux/usersSlice'

const columns: TableColumnsType<User> = [
  {
    title: 'Name',
    dataIndex: 'name',
    sorter: true,
  },
  {
    title: 'Email',
    dataIndex: 'email',
  },
  {
    title: 'Phone',
    dataIndex: 'phone',
  },
  {
    title: 'Business Type',
    dataIndex: 'businessType',
  },
  {
    title: 'Identifier',
    dataIndex: 'identifier',
  },
  {
    title: 'Registered',
    dataIndex: 'registeredAt',
    render: (value) => value && dayjs(value).format("DD/MM/YYYY"),
    sorter: true,
  },
  {
    title: 'Actions',
    render: (value: User) => {
      return (
        <Flex gap={10}>
          {value.bannedAt ? (
            <Tooltip title="Banned">
              <Button /* onClick={} */ icon={<FaBan />} size="small" />

            </Tooltip>
          ) : (
            value.validatedAt ? (
              <Tooltip title="Validated">
                <Button /* onClick={} */ icon={<IoCheckmarkDoneOutline />} size="small" />
              </Tooltip>
            ) : (
              <Tooltip title="Waits validation">
                <Button /* onClick={} */ icon={<GoLock />} size="small" />
                
              </Tooltip>
            )
          )}

          <Link to={`/users/clients/${value._id}/modify`}>
            <Button /* onClick={} */ icon={<FaUserEdit />} size="small" />
          </Link>
        </Flex>
      )
    },
  },
]

interface ClientTableParams {
  pagination: TablePaginationConfig
  orderBy?: UsersOrderBy
  orderDirection?: OrderDirection
  // filters?: Record<string, FilterValue | null>
}

export default function AllClients() {
  const dispatch = useAppDispatch()
  const { users, totalUsersCount } = useAppSelector((state) => state.user)

  const loading = useAttemptListener({
    attempt: attemptGetUsers,
    listenerMiddleware: listenerMiddleware,
    
    onRejected: (action) => {
      const errorMessage = action.error?.message || 'Failed to fetch users.'
      message.error(errorMessage)
    },
  })

  const [tableParams, setTableParams] = useState<ClientTableParams>({
    pagination: {
      current: 1,
      pageSize: 10,
      total: 0,
      showSizeChanger: true,
      pageSizeOptions: ['10', '20', '50', '100'],
    },
    orderBy: UsersOrderBy.REGISTERED_AT,
    orderDirection: OrderDirection.DESC,
  })

  useEffect(() => {
    const current = tableParams.pagination.current as number
    const pageSize = tableParams.pagination.pageSize as number
    const skip = (current - 1) * pageSize

    const payload: GetListUsersDto = {
      skip,
      limit: pageSize,
      orderBy: tableParams.orderBy,
      orderDirection: tableParams.orderDirection,
    }
    dispatch(attemptGetUsers(payload))
  }, [
    dispatch,
    tableParams.pagination.current,
    tableParams.pagination.pageSize,
    tableParams.orderBy,
    tableParams.orderDirection
  ])

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<User> | SorterResult<User>[]
  ) => {
    const currentSorter = Array.isArray(sorter) ? sorter[0] : sorter

    let newOrderBy: UsersOrderBy | undefined = tableParams.orderBy
    let newOrderDirection: OrderDirection | undefined = tableParams.orderDirection

    if (currentSorter && currentSorter.field) {
      switch (currentSorter.field) {
        case 'name':
          newOrderBy = UsersOrderBy.NAME
          break
        case 'registeredAt':
          newOrderBy = UsersOrderBy.REGISTERED_AT
          break
        default:
          newOrderBy = UsersOrderBy.REGISTERED_AT
      }
      if (currentSorter.order === 'ascend') {
        newOrderDirection = OrderDirection.ASC
      } else if (currentSorter.order === 'descend') {
        newOrderDirection = OrderDirection.DESC
      }
    }

    setTableParams(prev => ({
      pagination: {
        ...pagination,
        total: prev.pagination.total,
      },
      orderBy: newOrderBy,
      orderDirection: newOrderDirection,
    }))

  }

  return (
    <>
      <Table<User>
        size={'small'}
        sticky
        columns={columns}
        rowKey={(user) => user._id}
        dataSource={Object.values(users)}
        pagination={{
          ...tableParams.pagination,
          total: totalUsersCount,
        }}
        loading={loading}
        onChange={handleTableChange}
        // scroll={{ y: '70vh' }}
      />
    </>
  )
}